import * as React from 'react';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Chip from '@material-ui/core/Chip';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Grid from '@material-ui/core/Grid';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import MaterialUILink from '@material-ui/core/Link';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { Box, Link, Tooltip } from '@material-ui/core';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { DeepReadonly } from 'utility-types';
import { fetchNgqeClients } from 'slices/ngqeClients';
import { GridContainer } from '@pec/aion-ui-components/components/GridContainer';
import { IAddScoreResult } from 'interfaces/scoreResult';
import { IClient } from '@pec/aion-ui-core/interfaces/client';
import { IClientScoreResult } from 'interfaces/scoreResult';
import { localizeNumber } from '@pec/aion-ui-i18next/helpers/localize';
import { OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { RouteComponentProps, withRouter } from 'react-router';
import { Tile } from 'components/Tile';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useTypedSelector } from 'combineReducers';
import { v4 as uuid } from 'uuid';
import { translateScoreResult } from 'helpers/translateScoreResult';

export const scoreResultsTileStyles = (theme: Theme) =>
  createStyles({
    accordionDetails: {
      padding: `${theme.spacing()}px 0px`
    },
    chip: {
      color: 'white',
      fontWeight: 'bold',
      marginRight: theme.spacing(3)
    },
    panel: {
      borderTop: `1px solid ${theme.palette.divider}`,
      margin: `0 -${theme.spacing(1.5)}px`,
      '&:last-child': {
        marginBottom: `-${theme.spacing(1.5)}px`
      },
      '&.Mui-expanded': {
        margin: `0 -${theme.spacing(1.5)}px`
      }
    },
    firstPanel: {
      borderTop: 'none',
      '&.MuiAccordion-root:before': {
        backgroundColor: theme.palette.common.white
      }
    },
    notificationIcon: {
      paddingLeft: theme.spacing(0.5),
      color: theme.palette.primary.light
    },
    link: {
      textDecoration: 'none',
      color: 'var(--primary-blue-six)'
    },
    table: {
      '& td': {
        paddingLeft: theme.spacing(3)
      },
      '& th': {
        paddingLeft: theme.spacing(3)
      },
      '& tr:first-child td': {
        borderTop: `1px solid ${theme.palette.divider}`
      }
    },
    tableCell: {
      width: '50%'
    },
    statusIndicator: {
      borderRadius: 2,
      height: 16,
      width: 16
    }
  });

type RouteParams = {
  organizationId: string;
};

type OwnProps = {
  scoreResults: DeepReadonly<IClientScoreResult[]>;
  handleClick: (organizationId: string) => () => void;
  clientsForScores: IClient[];
};

type Props = WithStyles<typeof scoreResultsTileStyles> & RouteComponentProps<RouteParams> & OwnProps;

const ClientScoresTile: React.FC<Props> = ({
  match: {
    params: { organizationId }
  },
  scoreResults,
  clientsForScores,
  handleClick,
  classes
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language.split('-')[0];
  const email = 'safety@veriforce.com';
  const dispatch = useAppDispatch();
  const companyProfileLinkProps = { href: `/organizations/${organizationId}/profile` };
  const releasesLinkProps = { href: `/organizations/${organizationId}/profile/hiring-clients` };
  const [notificationStates, setNotificationStates] = React.useState<{ [key: string]: boolean }>({});
  const { ngqeClients } = useTypedSelector(state => state.ngqeClients);
  const { NgqeLegacyScoringDiscrepancies, QuestionnaireV2 } = OrganizationFeature;
  const organizationFeatures = [NgqeLegacyScoringDiscrepancies, QuestionnaireV2];

  const handleToggleShowNotification = (clientId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setNotificationStates(prevState => ({
      ...prevState,
      [clientId]: !prevState[clientId]
    }));
  };

  React.useEffect(() => {
    if (ngqeClients === null) {
      dispatch(fetchNgqeClients({ organizationId, organizationFeatures }));
    }
  }, [dispatch, organizationId, organizationFeatures, ngqeClients]);

  const filteredScoreResults = React.useMemo(() => {
    if (!clientsForScores) return [];

    const clientNameById = new Map(clientsForScores.map(client => [client.id, client.name]));

    return scoreResults
      .filter(scoreResult => clientNameById.has(scoreResult.clientId) && scoreResult.scores.length > 0)
      .map(scoreResult => translateScoreResult(scoreResult, language, clientNameById.get(scoreResult.clientId) || ''))
      .sort((a, b) => (a.clientName || '').localeCompare(b.clientName || ''));
  }, [scoreResults, clientsForScores, language]);

  return (
    <Tile primaryText={t('subscriber:dashboard.clientScoresTile.title', 'Client Scores')}>
      {filteredScoreResults && filteredScoreResults.length > 0 ? (
        filteredScoreResults.map((scoreResult, index) => (
          <Accordion
            elevation={0}
            key={scoreResult.clientId}
            expanded={scoreResult.isExpanded}
            className={index < 1 ? `${classes.panel} ${classes.firstPanel}` : classes.panel}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} onClick={handleClick(scoreResult.clientId)}>
              <GridContainer spacing={0} justify="space-between">
                <Grid item xs={9}>
                  <div>
                    <Typography variant="h6">
                      {scoreResult.clientName}
                      {ngqeClients?.some(client => client.id === scoreResult.clientId) && (
                        <span className={classes.notificationIcon}>
                          <InfoOutlinedIcon
                            onClick={event => handleToggleShowNotification(scoreResult.clientId, event)}
                          />
                        </span>
                      )}
                    </Typography>
                  </div>
                  {ngqeClients?.some(client => client.id === scoreResult.clientId) &&
                    notificationStates[scoreResult.clientId] && (
                      <Typography variant="subtitle2">
                        <Trans i18nKey="subscriber:dashboard.clientScoresTile.notification">
                          As part of the release of our enhanced Questionnaire, some changes to the way you are scored
                          by clients may have occurred. Note that this does not necessarily indicate inaccuracies. If
                          there are any inaccuracies, they will be corrected soon, and no action is required on your
                          part. If you have any concerns, please contact our Contractor Support Team at{' '}
                          <a
                            href={`mailto:${email}`}
                            onClick={e => {
                              e.stopPropagation();
                            }}
                            className={classes.link}
                          >
                            safety@veriforce.com
                          </a>
                          .
                        </Trans>
                      </Typography>
                    )}
                </Grid>
                {scoreResult.overallRanking && (
                  <Grid item>
                    <Chip
                      label={scoreResult.overallRanking.description}
                      style={{ backgroundColor: scoreResult.overallRanking.color }}
                      className={classes.chip}
                    />
                  </Grid>
                )}
              </GridContainer>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <TableContainer>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('subscriber:dashboard.clientScoresTile.scoreSet', 'Score Set')}</TableCell>
                      <TableCell>{t('subscriber:dashboard.clientScoresTile.status', 'Status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scoreResult.scores
                      .filter((score: IAddScoreResult) => score.isActive)
                      .sort((a: IAddScoreResult, b: IAddScoreResult) => {
                        return a.scoreSetName.localeCompare(b.scoreSetName);
                      })
                      .map((score: IAddScoreResult) => (
                        <TableRow key={uuid()}>
                          <TableCell>
                            <Link
                              href={`/organizations/${organizationId}/scoring/scoreSetResults/${score.scoreSetId}/clientId/${scoreResult.clientId}`}
                              underline="always"
                            >
                              <Typography variant="inherit">{score.scoreSetName || ''}</Typography>
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gridGap={8}>
                              <Box
                                className={classes.statusIndicator}
                                style={{
                                  backgroundColor: score.rankingColor,
                                  flexShrink: 0
                                }}
                              />
                              <Typography variant="inherit">
                                {score.rankingDescription}
                                {score.calculatedScore > 0 &&
                                  ` - ${localizeNumber(
                                    {
                                      value: score.calculatedScore,
                                      config: {
                                        style: 'percent',
                                        maximumFractionDigits: 0
                                      }
                                    },
                                    t
                                  )}`}
                              </Typography>
                              {score.scoreOverride && (
                                <Box style={{ marginLeft: 'auto' }}>
                                  <Tooltip
                                    placement="top"
                                    enterTouchDelay={0}
                                    title={
                                      t(
                                        'subscriber:dashboard.clientScoresTile.overrideApplied',
                                        'Override applied'
                                      ) as string
                                    }
                                  >
                                    <ReportProblemOutlinedIcon
                                      fontSize="small"
                                      color="primary"
                                      style={{ flexShrink: 0, display: 'block' }}
                                    />
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        <GridContainer>
          <Grid item xs={12}>
            <Typography variant="h6">
              {t('subscriber:dashboard.clientScoresTile.noClientScores', 'No Client Scores are Available')}
            </Typography>
            <Typography variant="body1">
              <Trans i18nKey="subscriber:dashboard.clientScoresTile.reminder">
                Please ensure your{' '}
                <MaterialUILink underline="always" {...companyProfileLinkProps}>
                  Company Profile
                </MaterialUILink>{' '}
                is completed and your{' '}
                <MaterialUILink underline="always" {...releasesLinkProps}>
                  Releases
                </MaterialUILink>{' '}
                are up-to-date.
              </Trans>
            </Typography>
          </Grid>
        </GridContainer>
      )}
    </Tile>
  );
};

export const ClientScoreResultsTileComponent = withStyles(scoreResultsTileStyles)(withRouter(ClientScoresTile));
